/**
 * 组件索引类型
 */
export var IndexType;
(function (IndexType) {
    /** 哈希索引 - 最快查找 */
    IndexType["HASH"] = "hash";
    /** 位图索引 - 内存高效 */
    IndexType["BITMAP"] = "bitmap";
    /** 排序索引 - 支持范围查询 */
    IndexType["SORTED"] = "sorted";
})(IndexType || (IndexType = {}));
/**
 * 哈希索引实现
 *
 * 使用Map数据结构，提供O(1)的查找性能。
 * 适合大多数查询场景。
 */
export class HashComponentIndex {
    constructor() {
        this.type = IndexType.HASH;
        this._componentToEntities = new Map();
        this._entityToComponents = new Map();
        this._queryCount = 0;
        this._totalQueryTime = 0;
        this._lastUpdated = Date.now();
        this._setPool = [];
        this._componentTypeSetPool = [];
    }
    addEntity(entity) {
        if (entity.components.length === 0) {
            const componentTypes = this._componentTypeSetPool.pop() || new Set();
            componentTypes.clear();
            this._entityToComponents.set(entity, componentTypes);
            this._lastUpdated = Date.now();
            return;
        }
        const componentTypes = this._componentTypeSetPool.pop() || new Set();
        componentTypes.clear();
        for (const component of entity.components) {
            const componentType = component.constructor;
            componentTypes.add(componentType);
            let entities = this._componentToEntities.get(componentType);
            if (!entities) {
                entities = this._setPool.pop() || new Set();
                entities.clear();
                this._componentToEntities.set(componentType, entities);
            }
            entities.add(entity);
        }
        this._entityToComponents.set(entity, componentTypes);
        this._lastUpdated = Date.now();
    }
    removeEntity(entity) {
        const componentTypes = this._entityToComponents.get(entity);
        if (!componentTypes)
            return;
        for (const componentType of componentTypes) {
            const entities = this._componentToEntities.get(componentType);
            if (entities) {
                entities.delete(entity);
                if (entities.size === 0) {
                    this._componentToEntities.delete(componentType);
                    if (this._setPool.length < 50) {
                        entities.clear();
                        this._setPool.push(entities);
                    }
                }
            }
        }
        this._entityToComponents.delete(entity);
        if (this._componentTypeSetPool.length < 50) {
            componentTypes.clear();
            this._componentTypeSetPool.push(componentTypes);
        }
        this._lastUpdated = Date.now();
    }
    query(componentType) {
        const startTime = performance.now();
        const entities = this._componentToEntities.get(componentType);
        const result = entities ? new Set(entities) : new Set();
        this._queryCount++;
        this._totalQueryTime += performance.now() - startTime;
        return result;
    }
    queryMultiple(componentTypes, operation) {
        const startTime = performance.now();
        if (componentTypes.length === 0) {
            return new Set();
        }
        if (componentTypes.length === 1) {
            return this.query(componentTypes[0]);
        }
        let result;
        if (operation === 'AND') {
            let smallestSet;
            let smallestSize = Infinity;
            for (const componentType of componentTypes) {
                const entities = this._componentToEntities.get(componentType);
                if (!entities || entities.size === 0) {
                    this._queryCount++;
                    this._totalQueryTime += performance.now() - startTime;
                    return new Set();
                }
                if (entities.size < smallestSize) {
                    smallestSize = entities.size;
                    smallestSet = entities;
                }
            }
            result = new Set();
            if (smallestSet) {
                for (const entity of smallestSet) {
                    let hasAll = true;
                    for (const componentType of componentTypes) {
                        const entities = this._componentToEntities.get(componentType);
                        if (!entities || !entities.has(entity)) {
                            hasAll = false;
                            break;
                        }
                    }
                    if (hasAll) {
                        result.add(entity);
                    }
                }
            }
        }
        else {
            result = new Set();
            for (const componentType of componentTypes) {
                const entities = this._componentToEntities.get(componentType);
                if (entities) {
                    for (const entity of entities) {
                        result.add(entity);
                    }
                }
            }
        }
        this._queryCount++;
        this._totalQueryTime += performance.now() - startTime;
        return result;
    }
    clear() {
        this._componentToEntities.clear();
        this._entityToComponents.clear();
        this._lastUpdated = Date.now();
    }
    getStats() {
        let memoryUsage = 0;
        memoryUsage += this._componentToEntities.size * 64;
        memoryUsage += this._entityToComponents.size * 64;
        for (const entities of this._componentToEntities.values()) {
            memoryUsage += entities.size * 8;
        }
        for (const components of this._entityToComponents.values()) {
            memoryUsage += components.size * 8;
        }
        return {
            type: this.type,
            size: this._componentToEntities.size,
            memoryUsage,
            queryCount: this._queryCount,
            avgQueryTime: this._queryCount > 0 ? this._totalQueryTime / this._queryCount : 0,
            lastUpdated: this._lastUpdated
        };
    }
}
/**
 * 位图索引实现
 *
 * 使用位操作进行快速集合运算，内存效率高。
 * 适合有限组件类型和大量实体的场景。
 */
export class BitmapComponentIndex {
    constructor() {
        this.type = IndexType.BITMAP;
        this._componentTypeToBit = new Map();
        this._entityToBitmap = new Map();
        this._bitToEntities = new Map();
        this._nextBit = 0;
        this._queryCount = 0;
        this._totalQueryTime = 0;
        this._lastUpdated = Date.now();
    }
    addEntity(entity) {
        let bitmap = 0;
        for (const component of entity.components) {
            const componentType = component.constructor;
            let bit = this._componentTypeToBit.get(componentType);
            if (bit === undefined) {
                bit = this._nextBit++;
                this._componentTypeToBit.set(componentType, bit);
            }
            bitmap |= (1 << bit);
            let entities = this._bitToEntities.get(1 << bit);
            if (!entities) {
                entities = new Set();
                this._bitToEntities.set(1 << bit, entities);
            }
            entities.add(entity);
        }
        this._entityToBitmap.set(entity, bitmap);
        this._lastUpdated = Date.now();
    }
    removeEntity(entity) {
        const bitmap = this._entityToBitmap.get(entity);
        if (bitmap === undefined)
            return;
        // 从所有相关的位集合中移除实体
        for (const [bitMask, entities] of this._bitToEntities) {
            if ((bitmap & bitMask) !== 0) {
                entities.delete(entity);
                if (entities.size === 0) {
                    this._bitToEntities.delete(bitMask);
                }
            }
        }
        this._entityToBitmap.delete(entity);
        this._lastUpdated = Date.now();
    }
    query(componentType) {
        const startTime = performance.now();
        const bit = this._componentTypeToBit.get(componentType);
        if (bit === undefined) {
            this._queryCount++;
            this._totalQueryTime += performance.now() - startTime;
            return new Set();
        }
        const result = new Set(this._bitToEntities.get(1 << bit) || []);
        this._queryCount++;
        this._totalQueryTime += performance.now() - startTime;
        return result;
    }
    queryMultiple(componentTypes, operation) {
        const startTime = performance.now();
        if (componentTypes.length === 0) {
            return new Set();
        }
        let targetBitmap = 0;
        const validBits = [];
        for (const componentType of componentTypes) {
            const bit = this._componentTypeToBit.get(componentType);
            if (bit !== undefined) {
                targetBitmap |= (1 << bit);
                validBits.push(1 << bit);
            }
        }
        const result = new Set();
        if (operation === 'AND') {
            for (const [entity, entityBitmap] of this._entityToBitmap) {
                if ((entityBitmap & targetBitmap) === targetBitmap) {
                    result.add(entity);
                }
            }
        }
        else {
            for (const bitMask of validBits) {
                const entities = this._bitToEntities.get(bitMask);
                if (entities) {
                    for (const entity of entities) {
                        result.add(entity);
                    }
                }
            }
        }
        this._queryCount++;
        this._totalQueryTime += performance.now() - startTime;
        return result;
    }
    clear() {
        this._componentTypeToBit.clear();
        this._entityToBitmap.clear();
        this._bitToEntities.clear();
        this._nextBit = 0;
        this._lastUpdated = Date.now();
    }
    getStats() {
        let memoryUsage = 0;
        memoryUsage += this._componentTypeToBit.size * 12;
        memoryUsage += this._entityToBitmap.size * 12;
        memoryUsage += this._bitToEntities.size * 64;
        for (const entities of this._bitToEntities.values()) {
            memoryUsage += entities.size * 8;
        }
        return {
            type: this.type,
            size: this._componentTypeToBit.size,
            memoryUsage,
            queryCount: this._queryCount,
            avgQueryTime: this._queryCount > 0 ? this._totalQueryTime / this._queryCount : 0,
            lastUpdated: this._lastUpdated
        };
    }
}
/**
 * 智能组件索引管理器
 *
 * 根据使用模式自动选择最优的索引策略。
 * 支持动态切换索引类型以获得最佳性能。
 */
export class ComponentIndexManager {
    constructor(initialType = IndexType.HASH) {
        this._indexHistory = new Map();
        this._autoOptimize = true;
        this._optimizationThreshold = 1000;
        this._activeIndex = this.createIndex(initialType);
    }
    /**
     * 添加实体到索引
     */
    addEntity(entity) {
        this._activeIndex.addEntity(entity);
        if (this._autoOptimize && this._activeIndex.getStats().queryCount % 100 === 0) {
            this.checkOptimization();
        }
    }
    /**
     * 从索引中移除实体
     */
    removeEntity(entity) {
        this._activeIndex.removeEntity(entity);
    }
    /**
     * 查询包含指定组件的实体
     */
    query(componentType) {
        return this._activeIndex.query(componentType);
    }
    /**
     * 批量查询多个组件
     */
    queryMultiple(componentTypes, operation) {
        return this._activeIndex.queryMultiple(componentTypes, operation);
    }
    /**
     * 手动切换索引类型
     */
    switchIndexType(type) {
        if (type === this._activeIndex.type)
            return;
        this._indexHistory.set(this._activeIndex.type, this._activeIndex.getStats());
        const oldIndex = this._activeIndex;
        this._activeIndex = this.createIndex(type);
        oldIndex.clear();
    }
    /**
     * 启用/禁用自动优化
     */
    setAutoOptimize(enabled) {
        this._autoOptimize = enabled;
    }
    /**
     * 获取当前索引统计信息
     */
    getStats() {
        return this._activeIndex.getStats();
    }
    /**
     * 获取所有索引类型的历史统计信息
     */
    getAllStats() {
        const current = this._activeIndex.getStats();
        return new Map([
            ...this._indexHistory,
            [current.type, current]
        ]);
    }
    /**
     * 清空索引
     */
    clear() {
        this._activeIndex.clear();
    }
    /**
     * 创建指定类型的索引
     */
    createIndex(type) {
        switch (type) {
            case IndexType.HASH:
                return new HashComponentIndex();
            case IndexType.BITMAP:
                return new BitmapComponentIndex();
            case IndexType.SORTED:
                return new HashComponentIndex();
            default:
                return new HashComponentIndex();
        }
    }
    /**
     * 检查是否需要优化索引
     */
    checkOptimization() {
        if (!this._autoOptimize)
            return;
        const stats = this._activeIndex.getStats();
        if (stats.queryCount < this._optimizationThreshold)
            return;
        if (stats.avgQueryTime > 1.0 && stats.type !== IndexType.HASH) {
            this.switchIndexType(IndexType.HASH);
        }
        else if (stats.memoryUsage > 10 * 1024 * 1024 && stats.type !== IndexType.BITMAP) {
            this.switchIndexType(IndexType.BITMAP);
        }
    }
}
//# sourceMappingURL=ComponentIndex.js.map